import React, { ChangeEvent, FunctionComponent, useContext, useEffect, useState } from 'react';
import { BaseLayout } from '../../Component/BaseLayout/BaseLayout';
import { ShoppingPlannerContext } from '../../Context/ShoppingPlannerContext';
import { Icon } from '../../Component/Icon/Icon';
import { Checkbox } from '../../Component/Checkbox/Checkbox';
import { useValuesFromParameters } from '../../Hook/UseValuesFromParameters';
import { Floors } from '../../Api/FloorsApi';
import { useResponsive } from '../../Hook/UseResponsive';

import './FloorPage.scss';
import '../../Component/Sidebar/Sidebar.scss';
import '../../Component/SelectionMarker/SelectionMarker.scss';

export interface FloorFilter {
    selectedFloorIds: string[];
    textFilter: string;
}

interface FloorFamily {
    id: string;
    label: string;
}
type FloorFamilies = FloorFamily[];

function getFloorFamiliesFromFloors(floors: Floors): FloorFamilies {
    // Use a Map to ensure we get every floorFamilyId only once
    return [
        ...new Map(
            floors.map((floor) => [
                floor.floorFamilyId,
                {
                    id: floor.floorFamilyId,
                    label: floor.floorFamilyLabel,
                },
            ])
        ).values(),
    ];
}

function groupBy<T, K extends keyof T>(items: T[], key: K): T[][] {
    const tmp = new Map<T[K], T[]>();
    for (const item of items) {
        const current = tmp.get(item[key]);
        if (current === undefined) {
            tmp.set(item[key], [item]);
        } else {
            current.push(item);
        }
    }
    return [...tmp.values()];
}

export const FloorPage: FunctionComponent = () => {
    const { floors, setSelectedDecor, selectedDecor, floorFilter, setFloorFilter, setLocked } =
        useContext(ShoppingPlannerContext);
    const { isLargerThanMd } = useResponsive();
    const floorFamilies = getFloorFamiliesFromFloors(floors);

    const filteredFloors = floors.filter((floor) => floorFilter.selectedFloorIds.includes(floor.floorFamilyId));
    const groupedFloors = groupBy(filteredFloors, 'floorFamilyId');

    const checked = (floorId: string) =>
        floorFilter.selectedFloorIds.some((filteredFloorId) => filteredFloorId === floorId);

    const addOrRemoveFilteredFloor = (floorId: string) => {
        if (checked(floorId)) {
            setFloorFilter((currentFloorFilter) => ({
                ...currentFloorFilter,
                selectedFloorIds: currentFloorFilter.selectedFloorIds.filter(
                    (selectedFloorId) => selectedFloorId !== floorId
                ),
            }));
        } else {
            setFloorFilter((currentFloorFilter) => ({
                ...currentFloorFilter,
                selectedFloorIds: [...currentFloorFilter.selectedFloorIds, floorId],
            }));
        }
    };

    const onTextFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            // select all floors after emptying text filter
            setFloorFilter({
                selectedFloorIds: floorFamilies.map((floorFamily) => floorFamily.id),
                textFilter: '',
            });
        } else {
            setFloorFilter((currentFloorFilter) => ({
                ...currentFloorFilter,
                textFilter: e.target.value,
            }));
        }
    };

    useValuesFromParameters();

    useEffect(() => {
        setLocked(selectedDecor === '');
    }, [selectedDecor]);

    return (
        <BaseLayout>
            <div className="shopping-planner-floor-page">
                {isLargerThanMd ? (
                    <aside className="shopping-planner-sidebar">
                        <TextFilter floorFilter={floorFilter} onTextFilterChange={onTextFilterChange} />
                        <FloorSelectionDesktop
                            addOrRemoveFilteredFloor={addOrRemoveFilteredFloor}
                            checked={checked}
                            floorFamilies={floorFamilies}
                        />
                    </aside>
                ) : null}
                <div className="shopping-planner__scroll-area">
                    {isLargerThanMd ? null : (
                        <aside className="shopping-planner-sidebar">
                            <TextFilter floorFilter={floorFilter} onTextFilterChange={onTextFilterChange} />

                            <FloorSelectionMobile
                                addOrRemoveFilteredFloor={addOrRemoveFilteredFloor}
                                checked={checked}
                                floorFamilies={floorFamilies}
                            />
                        </aside>
                    )}
                    <div className="shopping-planner-floor-page__decors">
                        {groupedFloors.map((floors) =>
                            floors.map((floor, floorIndex) => (
                                <React.Fragment key={floor.floorId}>
                                    {/* Only show the floor family label once, above the first floor */}
                                    {floorIndex > 0 ? null : (
                                        <h2 className="shopping-planner-floor-page__headline">
                                            {floor.floorTypeLabel}{' '}
                                            <span className={['shopping-planner-floor-page__floor-name'].join(' ')}>
                                                {floor.floorFamilyLabel}
                                            </span>
                                        </h2>
                                    )}
                                    {/* Only show floor labels when the family has more than one floor */}
                                    {floors.length < 2 ? null : (
                                        <h3 className="shopping-planner-floor-page__sub-headline">
                                            {floor.floorLabel}
                                        </h3>
                                    )}
                                    <ul>
                                        {floor.decors.map((decor) => (
                                            <li
                                                key={decor.articleNumber}
                                                className={[
                                                    'shopping-planner-floor-page-decor',
                                                    decor.articleNumber === selectedDecor
                                                        ? 'shopping-planner-floor-page-decor--selected'
                                                        : '',
                                                ].join(' ')}
                                                onClick={() => {
                                                    setSelectedDecor(decor.articleNumber);
                                                }}
                                            >
                                                {decor.articleNumber === selectedDecor ? (
                                                    <Icon icon="check" className="shopping-planner-selection-marker" />
                                                ) : null}
                                                <img src={decor.imageUri ?? ''}></img>
                                                <div className="shopping-planner-floor-page-decor__description">
                                                    <div className="shopping-planner-floor-page-decor__label">
                                                        {decor.label}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </BaseLayout>
    );
};

interface FloorSelectionDesktopProps {
    floorFamilies: FloorFamilies;
    checked: (floorId: string) => boolean;
    addOrRemoveFilteredFloor: (floorId: string) => void;
}
const FloorSelectionDesktop: FunctionComponent<FloorSelectionDesktopProps> = (props) => (
    <div className="shopping-planner-filter">
        <h3 className="shopping-planner-sidebar__headline">Bodenauswahl</h3>
        {props.floorFamilies.map((floorFamily) => (
            <Checkbox
                key={floorFamily.id}
                value={floorFamily.id}
                checked={props.checked(floorFamily.id)}
                onChange={() => props.addOrRemoveFilteredFloor(floorFamily.id)}
            >
                <span>{floorFamily.label}</span>
            </Checkbox>
        ))}
    </div>
);

interface FloorSelectionMobileProps {
    floorFamilies: FloorFamilies;
    checked: (floorId: string) => boolean;
    addOrRemoveFilteredFloor: (floorId: string) => void;
}
const FloorSelectionMobile: FunctionComponent<FloorSelectionMobileProps> = (props) => {
    const [showFloorSelection, setShowFloorSelection] = useState<boolean>(false);

    return (
        <div className="shopping-planner-filter">
            <h3
                className="shopping-planner-sidebar__headline"
                onClick={() => setShowFloorSelection((currentFloorSelection) => !currentFloorSelection)}
            >
                Bodenauswahl
                <Icon icon="filter" className={[showFloorSelection ? 'icon--mirror' : ''].join(' ')} />
            </h3>

            {showFloorSelection ? (
                <div className="shopping-planner-filter__floor-selection">
                    {props.floorFamilies.map((floorFamily) => (
                        <Checkbox
                            key={floorFamily.id}
                            value={floorFamily.id}
                            checked={props.checked(floorFamily.id)}
                            onChange={() => props.addOrRemoveFilteredFloor(floorFamily.id)}
                        >
                            <span>{floorFamily.label}</span>
                        </Checkbox>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

interface TextFilterProps {
    onTextFilterChange: (e: ChangeEvent<HTMLInputElement>) => void;
    floorFilter: FloorFilter;
}

const TextFilter: FunctionComponent<TextFilterProps> = (props) => (
    <div className="shopping-planner-floor-page__text-filter">
        <input
            value={props.floorFilter.textFilter}
            onChange={props.onTextFilterChange}
            placeholder="Artikelnummer, Boden, Dekor"
        />
        <Icon icon="search" />
    </div>
);
